import React, { useState } from 'react';
import { ehrApi } from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { 
  Database, Download, Search, FileJson, FileText, ArrowRight, 
  CheckCircle, Server, RefreshCw
} from 'lucide-react';
import { toast } from 'sonner';

export default function EHRIntegrationPage() {
  const [patientId, setPatientId] = useState('');
  const [fhirData, setFhirData] = useState(null);
  const [bundleData, setBundleData] = useState(null);
  const [exportData, setExportData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [exportFormat, setExportFormat] = useState('fhir');

  const lookupPatient = async () => {
    if (!patientId.trim()) {
      toast.error('Enter a patient ID');
      return;
    }
    setLoading(true);
    try {
      const [fhirRes, bundleRes] = await Promise.all([
        ehrApi.getPatientFHIR(patientId),
        ehrApi.getPatientBundle(patientId)
      ]);
      setFhirData(fhirRes.data);
      setBundleData(bundleRes.data);
      toast.success('FHIR data loaded');
    } catch (err) {
      toast.error('Patient not found or access denied');
    } finally {
      setLoading(false);
    }
  };

  const handleExport = async () => {
    setLoading(true);
    try {
      const res = await ehrApi.exportData(exportFormat);
      setExportData(res.data);
      toast.success(`Exported ${exportFormat.toUpperCase()} data`);
    } catch (err) {
      toast.error('Export failed');
    } finally {
      setLoading(false);
    }
  };

  const downloadJSON = (data, filename) => {
    const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="space-y-8" data-testid="ehr-integration-page">
      <div>
        <h1 className="text-3xl font-semibold text-[#0A2342]" style={{ fontFamily: 'Outfit, sans-serif' }}>
          EHR Integration
        </h1>
        <p className="text-[#64748B] mt-1">
          HL7/FHIR R4 compliant data exchange and interoperability
        </p>
      </div>

      {/* Status Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Card className="careiq-metric-card">
          <CardContent className="p-4 flex items-center gap-3">
            <div className="w-10 h-10 bg-[#166534]/10 rounded-lg flex items-center justify-center">
              <CheckCircle className="w-5 h-5 text-[#166534]" />
            </div>
            <div>
              <div className="text-sm font-medium text-[#0A2342]">FHIR R4</div>
              <div className="text-xs text-[#166534]">Supported</div>
            </div>
          </CardContent>
        </Card>
        <Card className="careiq-metric-card">
          <CardContent className="p-4 flex items-center gap-3">
            <div className="w-10 h-10 bg-[#166534]/10 rounded-lg flex items-center justify-center">
              <CheckCircle className="w-5 h-5 text-[#166534]" />
            </div>
            <div>
              <div className="text-sm font-medium text-[#0A2342]">HL7 v2.5</div>
              <div className="text-xs text-[#166534]">Supported</div>
            </div>
          </CardContent>
        </Card>
        <Card className="careiq-metric-card">
          <CardContent className="p-4 flex items-center gap-3">
            <div className="w-10 h-10 bg-[#0E7490]/10 rounded-lg flex items-center justify-center">
              <Server className="w-5 h-5 text-[#0E7490]" />
            </div>
            <div>
              <div className="text-sm font-medium text-[#0A2342]">API Status</div>
              <div className="text-xs text-[#166534]">Online</div>
            </div>
          </CardContent>
        </Card>
        <Card className="careiq-metric-card">
          <CardContent className="p-4 flex items-center gap-3">
            <div className="w-10 h-10 bg-[#0E7490]/10 rounded-lg flex items-center justify-center">
              <Database className="w-5 h-5 text-[#0E7490]" />
            </div>
            <div>
              <div className="text-sm font-medium text-[#0A2342]">Resources</div>
              <div className="text-xs text-[#64748B]">Patient, Obs, Med</div>
            </div>
          </CardContent>
        </Card>
      </div>

      <Tabs defaultValue="patient-lookup">
        <TabsList className="grid w-full grid-cols-3">
          <TabsTrigger value="patient-lookup">Patient FHIR Lookup</TabsTrigger>
          <TabsTrigger value="export">Bulk Export</TabsTrigger>
          <TabsTrigger value="docs">API Documentation</TabsTrigger>
        </TabsList>

        <TabsContent value="patient-lookup" className="mt-6">
          <Card className="careiq-card">
            <CardHeader>
              <CardTitle className="text-lg">Patient FHIR Resource Lookup</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex gap-2">
                <Input
                  placeholder="Enter patient ID..."
                  value={patientId}
                  onChange={(e) => setPatientId(e.target.value)}
                  data-testid="ehr-patient-id-input"
                />
                <Button onClick={lookupPatient} disabled={loading} className="bg-[#0E7490] hover:bg-[#0E7490]/90 text-white" data-testid="ehr-lookup-btn">
                  {loading ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
                  <span className="ml-2">Lookup</span>
                </Button>
              </div>

              {fhirData && (
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <h3 className="text-sm font-medium text-[#0A2342]">FHIR Patient Resource</h3>
                    <Button size="sm" variant="outline" onClick={() => downloadJSON(fhirData, `patient-${patientId}.json`)}>
                      <Download className="w-3 h-3 mr-1" /> Download
                    </Button>
                  </div>
                  <pre className="bg-[#0A2342] text-[#E2E8F0] rounded-lg p-4 overflow-auto max-h-64 text-xs">
                    {JSON.stringify(fhirData, null, 2)}
                  </pre>
                </div>
              )}

              {bundleData && (
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <h3 className="text-sm font-medium text-[#0A2342]">FHIR Bundle ({bundleData.total} resources)</h3>
                    <Button size="sm" variant="outline" onClick={() => downloadJSON(bundleData, `bundle-${patientId}.json`)}>
                      <Download className="w-3 h-3 mr-1" /> Download Bundle
                    </Button>
                  </div>
                  <pre className="bg-[#0A2342] text-[#E2E8F0] rounded-lg p-4 overflow-auto max-h-64 text-xs">
                    {JSON.stringify(bundleData, null, 2)}
                  </pre>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="export" className="mt-6">
          <Card className="careiq-card">
            <CardHeader>
              <CardTitle className="text-lg">Bulk Data Export</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex gap-4 items-end">
                <div>
                  <label className="text-sm font-medium text-[#0A2342]">Export Format</label>
                  <select
                    value={exportFormat}
                    onChange={(e) => setExportFormat(e.target.value)}
                    className="mt-1 block w-full rounded-md border border-[#E2E8F0] p-2 text-sm"
                    data-testid="export-format-select"
                  >
                    <option value="fhir">FHIR R4 Bundle</option>
                    <option value="hl7">HL7 v2.5 ADT</option>
                  </select>
                </div>
                <Button onClick={handleExport} disabled={loading} className="bg-[#0E7490] hover:bg-[#0E7490]/90 text-white" data-testid="export-btn">
                  <Download className="w-4 h-4 mr-2" />
                  Export
                </Button>
              </div>

              {exportData && (
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <h3 className="text-sm font-medium text-[#0A2342]">
                      Export Result ({exportData.total || exportData.message_count} records)
                    </h3>
                    <Button size="sm" variant="outline" onClick={() => downloadJSON(exportData, `ehr-export-${exportFormat}.json`)}>
                      <Download className="w-3 h-3 mr-1" /> Download
                    </Button>
                  </div>
                  <pre className="bg-[#0A2342] text-[#E2E8F0] rounded-lg p-4 overflow-auto max-h-96 text-xs">
                    {JSON.stringify(exportData, null, 2)}
                  </pre>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="docs" className="mt-6">
          <Card className="careiq-card">
            <CardHeader>
              <CardTitle className="text-lg">FHIR API Documentation</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {[
                  { method: 'GET', path: '/api/ehr/patient/{id}/fhir', desc: 'Get FHIR Patient resource' },
                  { method: 'GET', path: '/api/ehr/patient/{id}/bundle', desc: 'Get full FHIR Bundle (Patient + Medications + Observations)' },
                  { method: 'GET', path: '/api/ehr/export?format=fhir', desc: 'Bulk export in FHIR R4 format' },
                  { method: 'GET', path: '/api/ehr/export?format=hl7', desc: 'Bulk export in HL7 v2.5 ADT format' },
                ].map((ep, i) => (
                  <div key={i} className="flex items-start gap-4 p-4 rounded-lg bg-[#F8FAFC] border border-[#E2E8F0]">
                    <span className={`px-2 py-1 rounded text-xs font-mono font-bold ${ep.method === 'GET' ? 'bg-[#166534]/10 text-[#166534]' : 'bg-[#1A56DB]/10 text-[#1A56DB]'}`}>
                      {ep.method}
                    </span>
                    <div>
                      <code className="text-sm font-mono text-[#0A2342]">{ep.path}</code>
                      <p className="text-xs text-[#64748B] mt-1">{ep.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
